import { Link } from 'react-router-dom';

interface ComicCardProps {
  id: string;
  title: string;
  slug: string;
  author: string;
  category: string;
  status: string;
  rating: number;
  views: number;
  chapters: number;
  updatedAt: string;
  coverImage: string;
  tags: string[];
  variant?: 'default' | 'compact' | 'featured';
  index?: number;
}

export default function ComicCard({ title, slug, author, status, rating, views, chapters, coverImage, tags, variant = 'default', index }: ComicCardProps) {
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toLocaleString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${slug}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
            }`}>
              {status}
            </span>
          </div>
          {index !== undefined && index < 3 && (
            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center">
              <span className="text-xs font-bold text-background-50">{index + 1}</span>
            </div>
          )}
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">{title}</h3>
        <p className="text-xs text-foreground-500 mt-1">{author}</p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${slug}`}
        className="group flex gap-4 bg-background-50 rounded-xl p-3 hover:bg-background-100 transition-colors cursor-pointer border border-background-200/60"
      >
        <div className="relative overflow-hidden rounded-lg w-24 h-32 flex-shrink-0">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        </div>
        <div className="flex flex-col justify-between py-1">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">{title}</h3>
            <p className="text-xs text-foreground-500 mt-0.5">{author}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="flex items-center gap-0.5 text-xs text-amber-500">
                <i className="ri-star-fill text-xs w-3 h-3 flex items-center justify-center"></i>
                {rating}
              </span>
              <span className="text-xs text-foreground-400">{formatViews(views)} 阅读</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-2 py-0.5 text-[10px] bg-accent-100 text-accent-700 rounded-full">{tag}</span>
            ))}
            <span className="text-[10px] text-foreground-400">共{chapters}话</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${slug}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
          <p className="text-xs text-background-50 line-clamp-3">点击查看详情</p>
        </div>
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
          }`}>
            {status}
          </span>
        </div>
        {index !== undefined && index < 3 && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center">
            <span className="text-xs font-bold text-background-50">{index + 1}</span>
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-foreground-950/70 px-2 py-0.5 rounded text-[10px] text-background-50">
          {rating}分
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">{title}</h3>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-foreground-500">{author}</p>
        <span className="text-[10px] text-primary-500">{chapters}话</span>
      </div>
    </Link>
  );
}